/**
 * CheckedIn（出社済み画面）
 * 詳細設計書 §4.2 SC-L01-B に対応。
 *
 * Props:
 *   assignment   {object}  my-seat API レスポンスの data（seat_number, check_in_time）
 *   onCheckOut   {function} 退社完了コールバック（checkOutData を渡す）
 */
import { useState, useEffect } from 'react';
import SeatMap from './SeatMap';
import CheckOutModal from './CheckOutModal';
import { getSeatMap } from '../api/lifeApi';

function formatTime(isoString) {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CheckedIn({ assignment, onCheckOut }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [seatMapData, setSeatMapData] = useState({ seats: [], my_seat_number: null });
  const [seatMapLoading, setSeatMapLoading] = useState(true);

  const seatNumber = assignment?.seat_number ?? '---';
  const checkInTime = assignment?.check_in_time ?? null;

  useEffect(() => {
    setSeatMapLoading(true);
    getSeatMap()
      .then((res) => {
        setSeatMapData(res?.data ?? { seats: [], my_seat_number: null });
      })
      .catch(() => {
        // 失敗しても画面は表示する（マップのみ空）
      })
      .finally(() => setSeatMapLoading(false));
  }, []);

  const handleCheckOutConfirm = (checkOutData) => {
    setModalOpen(false);
    onCheckOut(checkOutData);
  };

  return (
    <>
      {/* 座席ヒーロー */}
      <div className="life-seat-hero">
        <span className="life-seat-hero__icon">📍</span>
        <div className="life-seat-hero__number" aria-label={`本日の座席: ${seatNumber}`}>
          {seatNumber}
        </div>
        <div className="life-seat-hero__time">出社時刻: {formatTime(checkInTime)}</div>
      </div>

      {/* 座席マップカード */}
      <div className="life-card">
        <p className="life-card__title">🗺️ 座席位置</p>
        <div className="life-card__divider" />
        {seatMapLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <span className="life-spinner life-spinner--dark" aria-label="読み込み中" />
          </div>
        ) : (
          <SeatMap
            seats={seatMapData.seats}
            mySeatNumber={seatMapData.my_seat_number ?? seatNumber}
          />
        )}
      </div>

      {/* 座席情報カード */}
      <div className="life-card">
        <p className="life-card__title">💡 座席情報</p>
        <div className="life-card__divider" />
        <ul style={{ margin: 0, padding: '0 0 0 16px', fontSize: 13, color: 'var(--life-muted-foreground)', lineHeight: 1.8 }}>
          <li>この座席は退社処理まで変更できません</li>
          <li>退社処理後は本日中の再出社登録はできません</li>
        </ul>
      </div>

      {/* 退社ボタン */}
      <button
        className="life-btn life-btn--destructive"
        style={{ width: '100%' }}
        onClick={() => setModalOpen(true)}
      >
        🚪 退社する
      </button>

      {/* 退社確認モーダル */}
      <CheckOutModal
        isOpen={modalOpen}
        seatNumber={seatNumber}
        checkInTime={checkInTime}
        onConfirm={handleCheckOutConfirm}
        onCancel={() => setModalOpen(false)}
      />
    </>
  );
}

export default CheckedIn;
